import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  Slider,
  TextField,
  FormControl,
  FormLabel,
  Divider,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Save, CheckCircle, Delete, ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';

interface CaseReviewQuestion {
  id: string;
  question_text: string;
  question_type: 'YES_NO' | 'SCALE_1_10' | 'TEXT' | 'SCALE_1_5';
  order: number;
  is_required: boolean;
  help_text: string;
  is_active: boolean;
}

interface CaseReview {
  id: string;
  responses: { [questionId: string]: any };
  reviewed_at: string;
  reviewer: {
    id: number;
    username: string;
    first_name: string;
    last_name: string;
    email: string;
    full_name: string;
  };
}

interface CaseReviewFormProps {
  caseId: string;
}

export const CaseReviewForm: React.FC<CaseReviewFormProps> = ({ caseId }) => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<CaseReviewQuestion[]>([]);
  const [myReview, setMyReview] = useState<CaseReview | null>(null);
  const [allReviews, setAllReviews] = useState<CaseReview[]>([]);
  const [responses, setResponses] = useState<{ [questionId: string]: any }>({});
  const [initialResponses, setInitialResponses] = useState<{ [questionId: string]: any }>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loadingNextReview, setLoadingNextReview] = useState(false);

  useEffect(() => {
    fetchQuestionsAndReview();
  }, [caseId]);

  // Detect changes in responses
  useEffect(() => {
    const hasChanged = JSON.stringify(responses) !== JSON.stringify(initialResponses);
    setHasChanges(hasChanged);
  }, [responses, initialResponses]);

  const fetchQuestionsAndReview = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch questions
      const questionsResponse = await api.get('/review-questions/');
      const questionsData = questionsResponse.data.results || questionsResponse.data || [];
      setQuestions(questionsData);

      // Fetch my review
      const myReviewResponse = await api.get(`/cases/${caseId}/reviews/my-review/`);
      if (myReviewResponse.status === 200 && myReviewResponse.data) {
        setMyReview(myReviewResponse.data);
        const reviewResponses = myReviewResponse.data.responses || {};
        setResponses(reviewResponses);
        setInitialResponses(reviewResponses);
        setHasChanges(false);
      } else {
        // No existing review
        setMyReview(null);
        setResponses({});
        setInitialResponses({});
        setHasChanges(false);
      }

      // Fetch all reviews for this case
      const allReviewsResponse = await api.get(`/cases/${caseId}/reviews/`);
      setAllReviews(allReviewsResponse.data.results || allReviewsResponse.data || []);
    } catch (err: any) {
      console.error('Failed to fetch questions/review:', err);
      if (err.response?.status === 204) {
        // No review exists yet - this is fine
        setMyReview(null);
        setResponses({});
        setInitialResponses({});
        setHasChanges(false);
      } else {
        setError('Failed to load review questions');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResponseChange = (questionId: string, value: any) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Validate required fields with conditional logic
      const firstQuestion = questions.find(q => q.order === 1);
      const secondQuestion = questions.find(q => q.order === 2);

      for (const question of questions) {
        let isRequired = question.is_required;

        // Special case: If question 2 and question 1 was answered "NO", make question 2 not required
        if (question.order === 2 && firstQuestion && secondQuestion) {
          const firstQuestionAnswer = responses[firstQuestion.id];
          if (firstQuestionAnswer === false) {
            isRequired = false;
          }
        }

        if (isRequired) {
          const value = responses[question.id];
          if (value === null || value === undefined || value === '') {
            setError(`Please answer: ${question.question_text}`);
            setSaving(false);
            return;
          }
        }
      }

      const payload = {
        responses,
      };

      if (myReview) {
        // Update existing review
        await api.patch(`/cases/${caseId}/reviews/${myReview.id}/`, payload);
        setSuccessMessage('Review updated successfully!');
      } else {
        // Create new review
        await api.post(`/cases/${caseId}/reviews/`, payload);
        setSuccessMessage('Review submitted successfully!');
      }

      // Refresh the data
      await fetchQuestionsAndReview();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Failed to submit review:', err);
      setError(err.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!myReview) return;

    setDeleting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await api.delete(`/cases/${caseId}/reviews/${myReview.id}/`);
      setSuccessMessage('Review deleted successfully!');
      setDeleteDialogOpen(false);

      // Refresh the data
      await fetchQuestionsAndReview();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Failed to delete review:', err);
      setError(err.response?.data?.detail || 'Failed to delete review');
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleNextReview = async () => {
    setLoadingNextReview(true);
    try {
      const res = await api.get('/review-list/', {
        params: {
          page: 1,
          page_size: 1,
          has_reviewed: 'false',
          ordering: '-total_billed',
        }
      });

      if (res.data.results && res.data.results.length > 0) {
        const nextCase = res.data.results[0];
        navigate(`/cases/${nextCase.id}`);
      } else {
        setError('No more cases to review!');
        setTimeout(() => setError(null), 3000);
      }
    } catch (err: any) {
      console.error('Failed to fetch next review:', err);
      setError('Failed to load next review');
    } finally {
      setLoadingNextReview(false);
    }
  };

  const isQuestionRequired = (question: CaseReviewQuestion) => {
    // Check if question 2 should be conditionally not required
    if (question.order === 2 && question.is_required) {
      const firstQuestion = questions.find(q => q.order === 1);
      if (firstQuestion) {
        const firstQuestionAnswer = responses[firstQuestion.id];
        if (firstQuestionAnswer === false) {
          return false; // Not required if question 1 is "NO"
        }
      }
    }
    return question.is_required;
  };

  const renderQuestionInput = (question: CaseReviewQuestion) => {
    const value = responses[question.id];

    switch (question.question_type) {
      case 'YES_NO':
        return (
          <RadioGroup
            value={value === true ? 'yes' : value === false ? 'no' : ''}
            onChange={(e) => {
              const val = e.target.value;
              handleResponseChange(
                question.id,
                val === 'yes' ? true : false
              );
            }}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
          </RadioGroup>
        );

      case 'SCALE_1_10':
        return (
          <Box sx={{ px: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Slider
                value={value === null || value === undefined ? 1 : value}
                onChange={(_, newValue) => {
                  handleResponseChange(question.id, newValue);
                }}
                min={1}
                max={10}
                marks={[
                  { value: 1, label: '1' },
                  { value: 5, label: '5' },
                  { value: 10, label: '10' },
                ]}
                valueLabelDisplay="auto"
                sx={{ flexGrow: 1 }}
              />
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <Typography
                variant="h6"
                sx={{
                  minWidth: 40,
                  textAlign: 'center',
                  color: 'primary.main',
                  fontWeight: 700,
                }}
              >
                {value === null || value === undefined ? 1 : value}
              </Typography>
            </Box>
          </Box>
        );

      case 'SCALE_1_5':
        return (
          <Box sx={{ px: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Slider
                value={value === null || value === undefined ? 1 : value}
                onChange={(_, newValue) => {
                  handleResponseChange(question.id, newValue);
                }}
                min={1}
                max={5}
                marks={[
                  { value: 1, label: '1' },
                  { value: 3, label: '3' },
                  { value: 5, label: '5' },
                ]}
                valueLabelDisplay="auto"
                sx={{ flexGrow: 1 }}
              />
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <Typography
                variant="h6"
                sx={{
                  minWidth: 40,
                  textAlign: 'center',
                  color: 'primary.main',
                  fontWeight: 700,
                }}
              >
                {value === null || value === undefined ? 1 : value}
              </Typography>
            </Box>
          </Box>
        );

      case 'TEXT':
        return (
          <TextField
            fullWidth
            multiline
            minRows={3}
            maxRows={10}
            value={value || ''}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            placeholder="Enter your response..."
            variant="outlined"
          />
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading review form...
        </Typography>
      </Box>
    );
  }

  if (error && questions.length === 0) {
    return (
      <Box sx={{ px: 3, py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 3, py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          Case Review
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please answer the following questions about this case after reviewing the materials.
        </Typography>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert
          severity="success"
          icon={<CheckCircle />}
          sx={{ mb: 3 }}
          onClose={() => setSuccessMessage(null)}
        >
          {successMessage}
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Questions */}
      {questions.length === 0 ? (
        <Alert severity="info">
          No review questions have been configured yet. Please contact your administrator.
        </Alert>
      ) : (
        <Box>
          {questions.map((question, index) => (
            <Paper key={question.id} sx={{ p: 3, mb: 3 }}>
              <FormControl fullWidth>
                <FormLabel>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    {index + 1}. {question.question_text}
                    {isQuestionRequired(question) && (
                      <Typography component="span" color="error.main" sx={{ ml: 0.5 }}>
                        *
                      </Typography>
                    )}
                  </Typography>
                </FormLabel>

                {question.help_text && (
                  <FormHelperText sx={{ mt: 0, mb: 2 }}>{question.help_text}</FormHelperText>
                )}

                <Box sx={{ mt: 2 }}>{renderQuestionInput(question)}</Box>
              </FormControl>
            </Paper>
          ))}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4 }}>
            {/* Delete Button - Only show if user has already submitted a review */}
            {myReview ? (
              <Button
                variant="outlined"
                color="error"
                size="large"
                startIcon={<Delete />}
                onClick={handleDeleteClick}
                disabled={deleting || saving}
              >
                Delete Review
              </Button>
            ) : (
              <Box /> // Empty box for spacing
            )}

            {/* Submit/Update Button */}
            <Button
              variant="contained"
              size="large"
              startIcon={saving ? <CircularProgress size={20} /> : <Save />}
              onClick={handleSubmit}
              disabled={saving || deleting || !hasChanges}
            >
              {saving ? 'Saving...' : myReview ? 'Update Review' : 'Submit Review'}
            </Button>
          </Box>

          {/* Next Review Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              variant="contained"
              size="medium"
              endIcon={loadingNextReview ? <CircularProgress size={18} color="inherit" /> : <ArrowForward />}
              onClick={handleNextReview}
              disabled={loadingNextReview}
              sx={{
                textTransform: 'none',
                fontSize: '0.9rem',
                fontWeight: 600,
                px: 3,
                py: 1,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              {loadingNextReview ? 'Loading...' : 'Next Review'}
            </Button>
          </Box>
        </Box>
      )}

      {/* Other Team Members' Reviews */}
      {allReviews.length > 0 && (
        <Box sx={{ mt: 6 }}>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Team Reviews ({allReviews.length})
          </Typography>

          {allReviews.map((review) => (
            <Paper key={review.id} sx={{ p: 3, mb: 2, bgcolor: 'grey.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {review.reviewer.full_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(review.reviewed_at).toLocaleString()}
                </Typography>
              </Box>

              {questions.map((question) => {
                const answer = review.responses[question.id];
                let displayValue = 'N/A';

                if (answer !== null && answer !== undefined) {
                  if (question.question_type === 'YES_NO') {
                    displayValue = answer ? 'Yes' : 'No';
                  } else if (
                    question.question_type === 'SCALE_1_10' ||
                    question.question_type === 'SCALE_1_5'
                  ) {
                    displayValue = String(answer);
                  } else {
                    displayValue = answer;
                  }
                }

                return (
                  <Box key={question.id} sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {question.question_text}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {displayValue}
                    </Typography>
                  </Box>
                );
              })}
            </Paper>
          ))}
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Review?</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete your review? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <Delete />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};